import React, { ReactNode, useContext, useEffect, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import Box from "@material-ui/core/Box";
import CustomDialog, { CustomDialogProps } from "../../../../../components/Dialog/CustomDialog";
import CustomTextField from "../../../../../components/Input/CustomTextField";
import CustomButton from "../../../../../components/Button/CustomButton";
import TestSeriesApi from "../../../../../api/services/TestSeriesApi";
import ErrorSnackbarContext from "../../../../../contexts/ErrorSnackbarContext";
import useEnv from "../../../../../hooks/env";
import ConfidentialConfirmationDialog from "../../../../../components/Dialog/ConfidentialConfirmationDialog";
import {
  containsHighlyConfidentialInfo,
  fetchHighlyConfidentialRegex,
} from "../../../../../functions/confidentialCheck.function";

type NonEmptyList = [number, ...number[]];

interface TestCasesNotesAddDialogProps extends CustomDialogProps {
  header: string;
  testCaseIds: number[];
  testSeriesId: number;
}

const TestCasesNotesAddDialog = (props: TestCasesNotesAddDialogProps) => {
  const { header, onClose, open, testCaseIds, testSeriesId } = props;
  const { api } = useEnv();
  const testSeriesApi = new TestSeriesApi(api);
  const { showSnackbar } = useContext(ErrorSnackbarContext);

  const yupValidationSchema = Yup.object().shape({
    note: Yup.string().required("Enter text to save a note"),
  });

  const [showConfidentialDialog, setShowConfidentialDialog] = useState(false);
  const [pendingNote, setPendingNote] = useState<string | null>(null);
  const [confidentialRegex, setConfidentialRegex] = useState<RegExp | null>(null);

  useEffect(() => {
    fetchHighlyConfidentialRegex().then(setConfidentialRegex);
  }, []);

  return (
    <>
      <CustomDialog fullWidth maxWidth="md" open={open} onClose={onClose}>
        <h2>{header}</h2>
        <Formik<{ note: string }>
          initialValues={{
            note: "",
          }}
          onSubmit={(values: { note: string }) => {
            if (confidentialRegex && containsHighlyConfidentialInfo([values.note], confidentialRegex)) {
              setPendingNote(values.note);
              setShowConfidentialDialog(true);
              return;
            }
            testSeriesApi
              .addNoteToTestCases(testSeriesId, {
                note: values.note,
                testCaseIds: testCaseIds as NonEmptyList,
              })
              .then(() => {
                showSnackbar("Notes saved successfully", "success");
                onClose();
              });
          }}
          validationSchema={yupValidationSchema}
        >
          {(formikProps: FormikProps<{ note: string }>): ReactNode => {
            // formik inner jsx form
            const { errors, handleBlur, handleChange, isSubmitting, touched, values } = formikProps;

            return (
              <Form>
                <CustomTextField
                  autoFocus
                  fullWidth
                  mandatory
                  helperText={errors.note && touched.note ? errors.note : ""}
                  multiline
                  rows={7}
                  name="note"
                  id="note"
                  label="Note"
                  value={values.note}
                  type="note"
                  error={!!(errors.note && touched.note)}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <Box display="flex" justifyContent="flex-end" marginTop="44px">
                  <CustomButton type="submit" primary disabled={isSubmitting} text="Save" />
                </Box>
              </Form>
            );
          }}
        </Formik>
      </CustomDialog>
      {showConfidentialDialog && (
        <ConfidentialConfirmationDialog
          open={showConfidentialDialog}
          objectName="note"
          onCancel={() => {
            setShowConfidentialDialog(false);
            setPendingNote(null);
          }}
          onProceed={() => {
            if (pendingNote) {
              testSeriesApi
                .addNoteToTestCases(testSeriesId, {
                  note: pendingNote,
                  testCaseIds: testCaseIds as NonEmptyList,
                })
                .then(() => {
                  showSnackbar("Notes saved successfully", "success");
                  onClose();
                });
            }
            setShowConfidentialDialog(false);
            setPendingNote(null);
          }}
        />
      )}
    </>
  );
};

export default TestCasesNotesAddDialog;
